import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Typography, LinearProgress, Snackbar } from '@mui/material';
import { API_BASE_URL } from 'src/apiConfig';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from 'src/scenes/students/administration/helper/const';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';

// Importujte vaš ConfirmDeleteDialog komponent

const SchoolsWithWorks = () => {
  const { t } = useTranslation();
  const [schools, setSchools] = useState([]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Stanje za snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Stanje za dijalog potvrde brisanja
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = () => {
    setIsLoading(true);
    fetch(`${API_BASE_URL}/api/connectingWork.php?action=getNotConnectWork`)
      .then((response) => response.json())
      .then((data) => setSchools(data))
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConnectClick = (schoolId) => {
    navigate(`/connect-work/${schoolId}`);
  };

  // Otvaranje dijaloga za potvrdu brisanja
  const handleDeleteClick = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setConfirmDialogOpen(true);
  };

  // Funkcija koja se poziva kada korisnik potvrdi brisanje
  const handleConfirmDelete = () => {
    setIsLoading(true);
    fetch(
      `${API_BASE_URL}/api/reviewAll.php?action=delete&schoolId=${selectedSchoolId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'success') {
          // Osvežavanje liste škola
          fetchSchools();
          // Prikazivanje snackbar poruke
          setSnackbarMessage(t('dialog.successfullyDeleted'));
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage(data.error);
          setSnackbarOpen(true);
          // Prikazivanje greške (po želji možete koristiti snackbar umesto alert)
          console.log(data.error || t('dialog.deleteFailed'));
        }
      })
      .catch((error) => {
        console.error('Error deleting works:', error);
        alert(t('dialog.deleteFailed'));
      })
      .finally(() => {
        setIsLoading(false);
        setConfirmDialogOpen(false);
        setSelectedSchoolId(null);
      });
  };

  // Zatvaranje dijaloga za potvrdu brisanja
  const handleCloseDeleteDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedSchoolId(null);
  };

  // Zatvaranje snackbar-a
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const columns = [
    { field: 'id', headerName: t('review.id'), width: 90 },
    { field: 'name', headerName: t('review.school_name'), width: 310 },
    { field: 'zipcode', headerName: t('location.zipcode'), width: 120 },
    { field: 'email', headerName: t('profile.email'), width: 250 },
    {
      field: 'totalStudentCountDB',
      headerName: t('review.student_count'),
      width: 150
    },
    {
      field: 'totalJpg',
      headerName: t('review.scanned_works_count'),
      width: 160
    },
    {
      field: 'studentCountConnect',
      headerName: t('review.connected_works_count'),
      width: 200
    },
    {
      field: 'action',
      headerName: t('review.action'),
      width: 300,
      renderCell: (params) => (
        <div>
          <Button
            variant="contained"
            color="primary"
            aria-hidden={false}
            onClick={() => handleConnectClick(params.row.id)}
            style={{
              backgroundColor: '#4cceac',
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            {t('review.connect')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            aria-hidden={false}
            onClick={() => handleDeleteClick(params.row.id)}
            style={{
              backgroundColor: '#e57373',
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px',
              marginLeft: '10px'
            }}
          >
            {t('review.deleteReview')}
          </Button>
        </div>
      )
    }
  ];

  const rows = schools.map((school) => ({
    id: school.id,
    name: school.name,
    zipcode: school.zipcode,
    email: school.email,
    totalStudentCountDB: school.totalStudentCountDB,
    totalJpg: school.totalJpg,
    studentCountConnect: school.studentCountConnect
  }));

  return (
    <div
      style={{
        height: 600,
        width: '100%',
        padding: '10px',
        borderRadius: '16px'
      }}
    >
      {isLoading && <LinearProgress />}

      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        checkboxSelection={false}
        loading={isLoading}
        components={{
          LoadingOverlay: LoadingOverlay
        }}
        disableColumnMenu
        componentsProps={{
          pagination: {
            labelRowsPerPage: t('pagination.rowsPerPage'),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${t('pagination.of')} ${
                count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
              }`
          }
        }}
        sx={{
          boxShadow: 2,
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#3788d8',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 'bold',
            borderBottom: '2px solid #ddd'
          },
          '& .MuiDataGrid-cell': {
            color: '#0d47a1',
            fontSize: '14px'
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: '#e3f2fd',
            borderTop: '1px solid #ddd'
          }
        }}
      />

      {/* Dijalog za potvrdu brisanja */}
      <ConfirmDeleteDialog
        open={confirmDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />

      {/* Snackbar za prikaz poruka */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </div>
  );
};

export default SchoolsWithWorks;
